/**
 * InternalMessaging - Internal messaging service for lukhas.team staff
 *
 * Features:
 * - Real-time messaging between team members
 * - Channel-based conversations
 * - Direct messages
 * - Message history
 * - Online status indicators
 * - Typing indicators
 */

import React, { useState, useRef, useEffect } from 'react'
import {
  MessageCircle,
  Send,
  Hash,
  Users,
  Search,
  Plus,
  MoreVertical,
  Paperclip,
  Smile,
  Check,
  CheckCheck,
  Clock,
  Circle
} from 'lucide-react'

interface User {
  id: string
  name: string
  avatar?: string
  role: string
  status: 'online' | 'away' | 'busy' | 'offline'
}

interface Message {
  id: string
  senderId: string
  content: string
  timestamp: number
  status: 'sending' | 'sent' | 'delivered' | 'read'
}

interface Channel {
  id: string
  name: string
  type: 'channel' | 'direct'
  participants: string[]
  unreadCount: number
  lastMessage?: Message
}

interface InternalMessagingProps {
  className?: string
  currentUserId?: string
}

// Demo data
const demoUsers: User[] = [
  { id: 'user_001', name: 'Alex Chen', role: 'Sr. Consciousness Engineer', status: 'online' },
  { id: 'user_002', name: 'Maya Patel', role: 'Lead Guardian Architect', status: 'online' },
  { id: 'user_003', name: 'James Wu', role: 'Memory Systems Dev', status: 'away' },
  { id: 'user_004', name: 'Sarah Kim', role: 'Security Engineer', status: 'busy' },
  { id: 'user_005', name: 'Marcus Brown', role: 'DevOps Lead', status: 'offline' },
]

const demoChannels: Channel[] = [
  { id: 'ch_001', name: 'general', type: 'channel', participants: ['user_001', 'user_002', 'user_003', 'user_004', 'user_005'], unreadCount: 0 },
  { id: 'ch_002', name: 'engineering', type: 'channel', participants: ['user_001', 'user_002', 'user_003'], unreadCount: 3 },
  { id: 'ch_003', name: 'guardian-protocols', type: 'channel', participants: ['user_002', 'user_004'], unreadCount: 1 },
  { id: 'dm_001', name: 'Maya Patel', type: 'direct', participants: ['user_001', 'user_002'], unreadCount: 2 },
  { id: 'dm_002', name: 'James Wu', type: 'direct', participants: ['user_001', 'user_003'], unreadCount: 0 },
]

const demoMessages: Record<string, Message[]> = {
  'ch_001': [
    { id: 'm1', senderId: 'user_002', content: 'Good morning team! Sprint review at 2pm today.', timestamp: Date.now() - 3600000, status: 'read' },
    { id: 'm2', senderId: 'user_003', content: "Sounds good, I'll prepare the memory core updates.", timestamp: Date.now() - 3500000, status: 'read' },
    { id: 'm3', senderId: 'user_001', content: 'Perfect. I have the consciousness integration metrics ready.', timestamp: Date.now() - 3400000, status: 'delivered' },
  ],
  'ch_002': [
    { id: 'm4', senderId: 'user_001', content: 'Has anyone reviewed the new guardian protocol PR?', timestamp: Date.now() - 7200000, status: 'read' },
    { id: 'm5', senderId: 'user_002', content: 'I left some comments. We need to revisit the authentication flow.', timestamp: Date.now() - 7100000, status: 'read' },
    { id: 'm6', senderId: 'user_003', content: "I'll take a look this afternoon.", timestamp: Date.now() - 7000000, status: 'delivered' },
  ],
  'dm_001': [
    { id: 'm7', senderId: 'user_002', content: 'Hey, do you have a moment to discuss the consciousness router?', timestamp: Date.now() - 1800000, status: 'read' },
    { id: 'm8', senderId: 'user_001', content: "Sure! What's on your mind?", timestamp: Date.now() - 1700000, status: 'delivered' },
    { id: 'm9', senderId: 'user_002', content: "I think we should add more granular logging to the signal path.", timestamp: Date.now() - 1600000, status: 'delivered' },
  ],
}

export default function InternalMessaging({ className = '', currentUserId = 'user_001' }: InternalMessagingProps) {
  const [channels] = useState<Channel[]>(demoChannels)
  const [users] = useState<User[]>(demoUsers)
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(demoChannels[0])
  const [messages, setMessages] = useState<Record<string, Message[]>>(demoMessages)
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const currentUser = users.find(u => u.id === currentUserId)

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, selectedChannel])

  // Simulate typing indicator
  useEffect(() => {
    if (newMessage.length > 0) {
      setIsTyping(true)
    } else {
      setIsTyping(false)
    }
  }, [newMessage])

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedChannel) return

    const message: Message = {
      id: `m_${Date.now()}`,
      senderId: currentUserId,
      content: newMessage.trim(),
      timestamp: Date.now(),
      status: 'sending'
    }

    setMessages(prev => ({
      ...prev,
      [selectedChannel.id]: [...(prev[selectedChannel.id] || []), message]
    }))

    setNewMessage('')

    // Simulate message delivery
    setTimeout(() => {
      setMessages(prev => ({
        ...prev,
        [selectedChannel.id]: prev[selectedChannel.id].map(m =>
          m.id === message.id ? { ...m, status: 'delivered' } : m
        )
      }))
    }, 500)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-emerald-500'
      case 'away':
        return 'bg-yellow-500'
      case 'busy':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getMessageStatusIcon = (status: string) => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3 h-3 text-gray-500" />
      case 'sent':
        return <Check className="w-3 h-3 text-gray-500" />
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-gray-500" />
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-400" />
      default:
        return null
    }
  }

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()

    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' })
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  const getUserById = (id: string) => users.find(u => u.id === id)

  const channelMessages = selectedChannel ? messages[selectedChannel.id] || [] : []

  const filteredChannels = channels.filter(ch =>
    ch.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden flex h-[500px] ${className}`}>
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-700/50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-white flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-lambda-gold" />
              <span>Messages</span>
            </h3>
            <button className="p-1.5 rounded-lg hover:bg-gray-700/50 transition-colors">
              <Plus className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-lambda-gold/50"
            />
          </div>
        </div>

        {/* Channels List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-2">
              Channels
            </div>
            {filteredChannels.filter(ch => ch.type === 'channel').map(channel => (
              <button
                key={channel.id}
                onClick={() => setSelectedChannel(channel)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                  selectedChannel?.id === channel.id
                    ? 'bg-lambda-gold/20 text-lambda-gold'
                    : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Hash className="w-4 h-4" />
                  <span className="text-sm">{channel.name}</span>
                </div>
                {channel.unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-lambda-gold text-gray-900 rounded-full font-medium">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            ))}

            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-2 mt-4">
              Direct Messages
            </div>
            {filteredChannels.filter(ch => ch.type === 'direct').map(channel => {
              const otherUserId = channel.participants.find(p => p !== currentUserId)
              const otherUser = otherUserId ? getUserById(otherUserId) : null
              return (
                <button
                  key={channel.id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    selectedChannel?.id === channel.id
                      ? 'bg-lambda-gold/20 text-lambda-gold'
                      : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="text-xs">{channel.name[0]}</span>
                      </div>
                      {otherUser && (
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-gray-900 ${getStatusColor(otherUser.status)}`} />
                      )}
                    </div>
                    <span className="text-sm">{channel.name}</span>
                  </div>
                  {channel.unreadCount > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-lambda-gold text-gray-900 rounded-full font-medium">
                      {channel.unreadCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Current User */}
        {currentUser && (
          <div className="p-3 border-t border-gray-700/50 bg-gray-800/30">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <span className="text-sm">{currentUser.name[0]}</span>
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-800 ${getStatusColor(currentUser.status)}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{currentUser.name}</p>
                <p className="text-xs text-gray-500 truncate">{currentUser.role}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedChannel ? (
          <>
            {/* Channel Header */}
            <div className="px-4 py-3 border-b border-gray-700/50 bg-gray-800/30 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {selectedChannel.type === 'channel' ? (
                  <Hash className="w-5 h-5 text-gray-400" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                    <span className="text-sm">{selectedChannel.name[0]}</span>
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-white">{selectedChannel.name}</h4>
                  <p className="text-xs text-gray-500">
                    {selectedChannel.type === 'channel'
                      ? `${selectedChannel.participants.length} members`
                      : getUserById(selectedChannel.participants.find(p => p !== currentUserId) || '')?.role}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors">
                  <Users className="w-4 h-4 text-gray-400" />
                </button>
                <button className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors">
                  <MoreVertical className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {channelMessages.map((message, idx) => {
                const sender = getUserById(message.senderId)
                const isOwn = message.senderId === currentUserId
                const showAvatar = idx === 0 || channelMessages[idx - 1].senderId !== message.senderId

                return (
                  <div
                    key={message.id}
                    className={`flex items-end space-x-2 ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}
                  >
                    {showAvatar ? (
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex-shrink-0 flex items-center justify-center">
                        <span className="text-xs">{sender?.name[0]}</span>
                      </div>
                    ) : (
                      <div className="w-8" />
                    )}
                    <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                      {showAvatar && (
                        <p className={`text-xs text-gray-500 mb-1 ${isOwn ? 'text-right' : ''}`}>
                          {sender?.name}
                        </p>
                      )}
                      <div
                        className={`px-3 py-2 rounded-2xl ${
                          isOwn
                            ? 'bg-lambda-gold/20 text-white rounded-br-sm'
                            : 'bg-gray-800/50 text-gray-200 rounded-bl-sm'
                        }`}
                      >
                        <p className="text-sm">{message.content}</p>
                      </div>
                      <div className={`flex items-center space-x-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                        <span className="text-xs text-gray-500">{formatTime(message.timestamp)}</span>
                        {isOwn && getMessageStatusIcon(message.status)}
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {isTyping && selectedChannel.type === 'direct' && (
              <div className="px-4 py-1">
                <span className="text-xs text-gray-500">Someone is typing...</span>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-gray-700/50 bg-gray-800/30">
              <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                >
                  <Paperclip className="w-5 h-5 text-gray-400" />
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Message ${selectedChannel.type === 'channel' ? '#' : ''}${selectedChannel.name}`}
                  className="flex-1 bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-lambda-gold/50"
                />
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                >
                  <Smile className="w-5 h-5 text-gray-400" />
                </button>
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="p-2 rounded-lg bg-lambda-gold/20 text-lambda-gold hover:bg-lambda-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
